import type { SVGProps } from "react";

/*
 * The mission step states as glyphs, from Phosphor Icons (fill weight): check-fat,
 * lightning, lock-simple, star. Copied as SVG rather than adding the package for four icons.
 *
 * Phosphor Icons — MIT License, Copyright (c) 2023 Phosphor Icons.
 * https://github.com/phosphor-icons/core/blob/main/LICENSE
 */

type IconProps = SVGProps<SVGSVGElement>;

/** One Phosphor glyph (256 grid, filled, in the current colour). */
export const Glyph = ({ d, ...props }: IconProps & { d: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 256 256"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path d={d} />
  </svg>
);

/** Completed — the step's reward is the player's. Phosphor `check-fat` (fill). */
export const StepDoneIcon = (props: IconProps) => (
  <Glyph
    {...props}
    d="M243.31,90.91l-128.4,128.4a16,16,0,0,1-22.62,0l-71.62-72a16,16,0,0,1,0-22.61l20-20a16,16,0,0,1,22.58,0L104,144.22l96.76-95.57a16,16,0,0,1,22.59,0l19.95,19.54A16,16,0,0,1,243.31,90.91Z"
  />
);

/** Active — the step the player is on now. Phosphor `lightning` (fill). */
export const StepActiveIcon = (props: IconProps) => (
  <Glyph
    {...props}
    d="M213.85,125.46l-112,120a8,8,0,0,1-13.69-7l14.66-73.33L45.19,143.49a8,8,0,0,1-3-13l112-120a8,8,0,0,1,13.69,7L153.18,90.9l57.63,21.61a8,8,0,0,1,3,12.95Z"
  />
);

/** Locked — a step still ahead. Phosphor `lock-simple` (fill), redrawn with a much heavier
 *  shackle (36 units thick instead of 16) on a slightly shorter body: at bead size (14px) the
 *  original's thin arc read as a box, not a lock — the shackle is the silhouette. */
export const StepLockedIcon = (props: IconProps) => (
  <Glyph
    {...props}
    d="M208,96H180V64a52,52,0,0,0-104,0V96H48A16,16,0,0,0,32,112v96a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V112A16,16,0,0,0,208,96ZM112,64a16,16,0,0,1,32,0V96H112Z"
  />
);

/** The task that pays — in order, the step's last task (Sebastian, 2026-10-19: "the last task
 *  should use a star, since completing that gives the prize"). Phosphor `star` (fill). */
export const StepPrizeIcon = (props: IconProps) => (
  <Glyph
    {...props}
    d="M234.29,114.85l-45,38.83L203,211.75a16.4,16.4,0,0,1-24.5,17.82L128,198.49,77.47,229.57A16.4,16.4,0,0,1,53,211.75l13.76-58.07-45-38.83A16.46,16.46,0,0,1,31.08,86l59-4.76,22.76-55.08a16.36,16.36,0,0,1,30.27,0l22.75,55.08,59,4.76a16.46,16.46,0,0,1,9.37,28.86Z"
  />
);
